import { useEffect, useRef } from "react";
import { playMidnightChime } from "./chime";

const COLORS = ["#e9a8f2", "#f4a6c6", "#8fd3fe", "#f5e9ff", "#ff7aa8", "#c8b6ff"];
const DURATION = 6500;

function drawHeart(ctx, s) {
  ctx.beginPath();
  ctx.moveTo(0, s * 0.35);
  ctx.bezierCurveTo(s * 0.9, -s * 0.35, s * 0.45, -s * 1.05, 0, -s * 0.45);
  ctx.bezierCurveTo(-s * 0.45, -s * 1.05, -s * 0.9, -s * 0.35, 0, s * 0.35);
  ctx.fill();
}

function drawStar(ctx, s) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 ? s * 0.42 : s;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  ctx.closePath();
  ctx.fill();
}

function burst(list, w, h) {
  const x = w * (0.15 + Math.random() * 0.7);
  const y = h * (0.15 + Math.random() * 0.4);
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const count = w < 600 ? 40 : 64;
  for (let i = 0; i < count; i++) {
    const a = (Math.PI * 2 * i) / count + Math.random() * 0.2;
    const v = 3 + Math.random() * 5.5;
    list.push({
      x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
      life: 1, decay: 0.008 + Math.random() * 0.008,
      size: 4 + Math.random() * 6, rot: Math.random() * Math.PI, spin: (Math.random() - 0.5) * 0.2,
      shape: Math.random() < 0.45 ? "heart" : "star",
      color: Math.random() < 0.7 ? color : COLORS[Math.floor(Math.random() * COLORS.length)],
    });
  }
}

export function MidnightFireworks({ onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    void playMidnightChime();
  }, []);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const parts = [];
    const start = performance.now();
    let nextBurst = 0;
    let raf;
    const tick = (t) => {
      const el = t - start;
      const w = window.innerWidth, h = window.innerHeight;
      if (el < DURATION - 1800 && el >= nextBurst) {
        burst(parts, w, h);
        if (Math.random() < 0.5) burst(parts, w, h);
        nextBurst = el + 380 + Math.random() * 420;
      }
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.vx *= 0.985; p.vy = p.vy * 0.985 + 0.045;
        p.x += p.vx; p.y += p.vy; p.rot += p.spin; p.life -= p.decay;
        if (p.life <= 0) { parts.splice(i, 1); continue; }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.min(1, p.life * 1.4);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 14;
        p.shape === "heart" ? drawHeart(ctx, p.size) : drawStar(ctx, p.size);
        ctx.restore();
      }
      if (el < DURATION || parts.length) raf = requestAnimationFrame(tick);
      else onDone?.();
    };
    raf = requestAnimationFrame(tick);
    const hardStop = window.setTimeout(() => onDone?.(), DURATION + 2500);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(hardStop);
      window.removeEventListener("resize", resize);
    };
  }, [onDone]);
  return <div data-testid="midnight-fireworks" aria-hidden="true" className="pointer-events-none fixed inset-0 z-[150]">
      <canvas ref={ref} className="h-full w-full" />
    </div>;
}
